import React, {useCallback, useEffect, useRef} from 'react';
import {useParams} from 'react-router-dom';
import {CONTENT_SUBMISSION_ANALYTICS_SCREENS as ANALYTICS_SCREENS} from '@/lib/analytics/analyticsConstants';
import animateCardToTarget from '@/utils/animateCardToTarget';
import {cleanupMarkedContentSubmissions} from '@/utils/cache-updaters/contentSubmissions';
import queryClient from '@/utils/reactQueryClient';
import type {Phase} from '@/config/contentSubmissionPhases';
import {PHASE} from '@/config/contentSubmissionPhases';
import {ContentSubmission} from '@/types/contentSubmission';
import EmptyState from '@/views/campaigns/components/content-submissions/empty-state';
import getEmptyStateContent from '@/views/campaigns/components/content-submissions/empty-state/getEmptyStateContent';
import {ContentSubmissionAction} from '@/components/content-submission-card';
import InfiniteLoader from '@/components/infinite-loader';
import Link from '@/components/link';
import VirtualizedMasonryGrid from '@/components/virtualized-masonry-grid';
import {CAMPAIGN_CONTENT_SUBMISSIONS_QUERY_KEY} from '@/hooks/queries/use-campaign-content-submissions-query';
import useContentSubmissionAction from '@/hooks/use-content-submission-action';
import ContentSubmissionCardItem from './ContentSubmissionCardItem';
import styles from './ContentSubmissionsGrid.module.scss';

const MIN_COLUMN_WIDTH = 280;
const MAX_COLUMN_WIDTH = 320;
const COLUMN_GUTTER = 16;

type ContentSubmissionsGridProps = {
  contentSubmissions: ContentSubmission[];
  campaignNetwork?: string | null;
  campaignState?: string | null;
  getCardLink: (cs: ContentSubmission) => string;
  activeFiltersKey?: string;
  phaseTotalCount?: number;
  sortBy?: string;
  sortDir?: string;
  selectedPhase?: Phase;
  onEndReached?: () => void;
  isLoadingInitial?: boolean;
  isLoadingMore?: boolean;
  scrollRef: React.RefObject<HTMLElement>;
  isAdmin?: boolean;
  isOffPlatform?: boolean;
  onTransitionSuccess?: (contentSubmissionId: string) => void;
  onCardClick?: (context: {creator_id?: string; flow_id: string}) => void;
  onCardMenuOpen?: (context: {creator_id?: string; flow_id: string}) => void;
  onCardActionSelected?: (
    action: ContentSubmissionAction,
    context: {creator_id?: string; flow_id: string}
  ) => void;
  screenName?: string;
};

function ContentSubmissionsGrid({
  contentSubmissions,
  campaignNetwork,
  campaignState,
  getCardLink,
  activeFiltersKey,
  phaseTotalCount,
  sortBy,
  sortDir,
  selectedPhase = PHASE.ALL,
  onEndReached,
  isLoadingInitial,
  isLoadingMore,
  scrollRef,
  isAdmin = false,
  isOffPlatform = false,
  onTransitionSuccess,
  onCardClick,
  onCardMenuOpen,
  onCardActionSelected,
  screenName = ANALYTICS_SCREENS.ASSETS_GRID,
}: ContentSubmissionsGridProps) {
  const {campaignId} = useParams<{campaignId: string}>();

  const onBeforeTransition = useCallback(
    async (id: string, targetPhase: Phase) => {
      const cardEl = document
        .querySelector<HTMLElement>(
          `[data-testid="content-submission-card"][data-id="${id}"]`
        )
        ?.closest<HTMLElement>('a');
      const sidebarEl = document.querySelector<HTMLElement>(
        `[data-testid="sidebar-item-${targetPhase}"]`
      );
      if (cardEl && sidebarEl) {
        await animateCardToTarget(cardEl, sidebarEl);
      }
    },
    []
  );

  const {handleAction: handleSubmissionAction, actionComponent} =
    useContentSubmissionAction({
      campaignId: campaignId ?? '',
      contentSubmissions,
      isOffPlatform,
      screenName,
      onBeforeTransition,
      onSuccess: onTransitionSuccess,
    });

  // masonic grid does not allow to pass fewer items without remounting,
  // so we stringify the filters to create a key that changes only when the filters change
  const gridKey = activeFiltersKey;

  // Cleanup marked (transitioned) items when navigating between phases/filters or unmounting.
  useEffect(() => {
    if (!gridKey) {
      return undefined;
    }
    return () => {
      if (campaignId) {
        cleanupMarkedContentSubmissions(campaignId);
        queryClient.invalidateQueries({
          queryKey: [CAMPAIGN_CONTENT_SUBMISSIONS_QUERY_KEY, campaignId],
        });
      }
    };
  }, [gridKey, campaignId]);

  const handleSubmissionActionRef = useRef(handleSubmissionAction);
  handleSubmissionActionRef.current = handleSubmissionAction;
  const getCardLinkRef = useRef(getCardLink);
  getCardLinkRef.current = getCardLink;
  const onCardClickRef = useRef(onCardClick);
  onCardClickRef.current = onCardClick;
  const onCardMenuOpenRef = useRef(onCardMenuOpen);
  onCardMenuOpenRef.current = onCardMenuOpen;
  const onCardActionSelectedRef = useRef(onCardActionSelected);
  onCardActionSelectedRef.current = onCardActionSelected;

  const renderItem = useCallback<
    (props: {data: ContentSubmission; index: number}) => React.ReactElement
  >(
    ({data}) => (
      <Link
        to={getCardLinkRef.current(data)}
        className={styles.cardLink}
        onClick={() =>
          onCardClickRef.current?.({
            creator_id: data.profile?.id,
            flow_id: data.id,
          })
        }
      >
        <ContentSubmissionCardItem
          contentSubmission={data}
          campaignId={campaignId}
          campaignNetwork={campaignNetwork}
          campaignState={campaignState}
          onAction={handleSubmissionActionRef.current}
          isAdmin={isAdmin}
          isOffPlatform={isOffPlatform}
          onMenuOpen={onCardMenuOpenRef.current}
          onActionSelected={onCardActionSelectedRef.current}
        />
      </Link>
    ),
    [campaignNetwork, campaignState, isAdmin, isOffPlatform, campaignId]
  );

  const isEmpty = !isLoadingInitial && contentSubmissions.length === 0;
  const emptyContent = getEmptyStateContent(selectedPhase);

  return (
    <>
      {actionComponent}
      {isLoadingInitial && !contentSubmissions.length ? (
        <InfiniteLoader isLoading />
      ) : null}
      {isEmpty ? (
        <EmptyState
          title={emptyContent.title}
          description={emptyContent.description}
        />
      ) : (
        <VirtualizedMasonryGrid<ContentSubmission>
          key={gridKey}
          items={contentSubmissions}
          minColumnWidth={MIN_COLUMN_WIDTH}
          maxColumnWidth={MAX_COLUMN_WIDTH}
          columnGutter={COLUMN_GUTTER}
          onEndReached={onEndReached}
          isLoadingMore={isLoadingMore}
          scrollRef={scrollRef}
          scrollRestorationKey={`content-submissions-grid-${gridKey}`}
          positionerDep={`${phaseTotalCount}-${sortBy ?? ''}-${sortDir ?? ''}`}
          renderItem={renderItem}
        />
      )}
    </>
  );
}

export default ContentSubmissionsGrid;
